import type { LiveAudioVisualizerV2Props } from '../LiveAudioVisualizerV2.types';
import React, { useEffect, useRef } from 'react';

// Exponential sensitivity constant
const EXP_K = 15;
// Minimum height for bars to ensure visibility
const MIN_BAR_HEIGHT = 3.2;
// Default smoothing factor
const SMOOTHING = 0.25;

export const LiveAudioVisualizerCanvas: React.FC<LiveAudioVisualizerV2Props> = ({
    pcmChunks,
    width = 115,
    height = 25,
    backgroundColor = 'transparent',
    barColor = 'yellow',
    barWidth = 2.5,
    gap = 1.5,
}) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const animationRef = useRef<number>(0);
    const barHeightsRef = useRef<number[]>([]);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        // measure the CSS size you just applied below
        const { width: cw, height: ch } = canvas.getBoundingClientRect();
        canvas.width = Math.round(cw);
        canvas.height = Math.round(ch);

        const totalBarSpace = barWidth + gap;
        const maxBars = Math.floor(cw / totalBarSpace);
        barHeightsRef.current = new Array(maxBars).fill(MIN_BAR_HEIGHT);

        const render = () => {
            const data = pcmChunks?.length ? pcmChunks[pcmChunks.length - 1] : null;
            const len = data?.length ?? 0;

            // clear & background
            ctx.clearRect(0, 0, cw, ch);
            ctx.fillStyle = backgroundColor;
            ctx.fillRect(0, 0, cw, ch);

            for (let i = 0; i < maxBars; i++) {
                let rawAmp = 0;
                if (len > 0) {
                    const frac = i / (maxBars - 1);
                    const idx = Math.floor(frac * (len - 1));
                    rawAmp = Math.abs(data![idx] || 0);
                }
                const boosted = 1 - Math.exp(-EXP_K * rawAmp);
                const targetHeight = Math.max(boosted * ch, MIN_BAR_HEIGHT);
                const current = barHeightsRef.current[i];
                barHeightsRef.current[i] = current + (targetHeight - current) * SMOOTHING;

                const h = barHeightsRef.current[i];
                const x = i * totalBarSpace;
                const y = (ch - h) / 2;
                ctx.fillStyle = barColor;
                ctx.fillRect(x, y, barWidth, h);
            }

            animationRef.current = requestAnimationFrame(render);
        };

        render();
        return () => cancelAnimationFrame(animationRef.current);
    }, [pcmChunks, width, height, backgroundColor, barColor, barWidth, gap]);

    return (
        <canvas
            ref={canvasRef}
            // intrinsic size is ignored here, we size via CSS+getBoundingClientRect
            style={{
                width: typeof width === 'number' ? `${width}px` : width,
                height: typeof height === 'number' ? `${height}px` : height,
            }}
        />
    );
};

LiveAudioVisualizerCanvas.displayName = 'LiveAudioVisualizerCanvas';
